import { EMPTY_CELL_HYPHEN, NON_BREAKING_SPACE } from "constants/index";

import { VFC } from "react";

import { Delete, KeyboardArrowRight } from "@material-ui/icons";
import { Box, portexColor } from "@portex-pro/ui-components";
import CursorPaginationTableControlsView from "components/pagination/CursorPaginationTableControlsView";
import TableView, { TableViewColumns, TableViewRows } from "components/TableView";
import Text from "components/Text";
import MultistopTooltip from "features/stops/MultistopTooltip";
import { getStopLabels } from "features/stops/utils/getStopLabels";
import isEmpty from "lodash/isEmpty";
import { useTranslation } from "react-i18next";
import arrayAt from "utils/arrayAt";

import { ShipperQuoteRequest } from "../../../../api/rest/quote-requests";

interface TemplatesTableViewProps {
  items: ShipperQuoteRequest[];
  onClickTemplate?: (quoteRequestId: ShipperQuoteRequest["id"]) => void;
  onDeleteTemplate?: (quoteRequestId: ShipperQuoteRequest["id"]) => void;
  loading?: boolean;
  paginationOptions?: {
    next: () => void;
    prev: () => void;
    setTake: (take: number) => void;
    take: number;
    page: number;
    total: number;
    paginationLimited?: boolean;
  };
}

const TemplatesTableView: VFC<TemplatesTableViewProps> = ({
  items,
  onClickTemplate,
  onDeleteTemplate,
  loading,
  paginationOptions,
}) => {
  const { t } = useTranslation("quoteRequestTemplates");

  const isTrucking = items.some((item) => item.mode === "FTL" || item.mode === "LTL");
  const isInternational = items.some((item) => item.mode === "FCL" || item.mode === "AIR");
  const isDrayage = items.some((item) => item.mode === "DRAYAGE");

  const columns: TableViewColumns<typeof items[number]> = [
    {
      name: t("originColumn"),
      renderCell: (item) => <TemplateStopCellView item={item} position="origin" />,
      cellProps: { style: { minWidth: 230, verticalAlign: "top" } },
    },
    {
      name: t("destinationColumn"),
      renderCell: (item) => <TemplateStopCellView item={item} position="destination" />,
      cellProps: { style: { minWidth: 230, verticalAlign: "top" } },
    },
    {
      name: t("incotermsColumn"),
      hideCell: isTrucking,
      renderCell: (item) => (
        <Text size="small" weight="bold">
          {item.incoterms}
        </Text>
      ),
      cellProps: { style: { verticalAlign: "top" } },
    },
    {
      name: t("routingColumn"),
      hideCell: isTrucking || isDrayage,
      renderCell: (item) => (
        <Text size="small" weight="bold">
          {item.routing_type}
        </Text>
      ),
      cellProps: { style: { verticalAlign: "top" } },
    },
    {
      name: t("equipmentColumn"),
      hideCell: isInternational,
      renderCell: (item) => <TemplateEquipmentCellView item={item} />,
      cellProps: { style: { verticalAlign: "top" } },
    },
  ];

  const rows: TableViewRows<typeof items[number]> = {
    rowBodyProps: { allWhite: true },
    headerCellProps: {
      style: {
        whiteSpace: "nowrap",
        background: portexColor.grey50,
        color: portexColor.grey500,
      },
    },
    renderRowStart: (item) =>
      item.template_name ? (
        <Text size="small" weight="bold" typographyProps={{ style: { color: portexColor.grey400 } }}>
          {item.template_name}
        </Text>
      ) : null,
    endingIcon: (item) => (
      <div className="flex space-x-2 justify-center items-center">
        <Box
          borderRadius="4px"
          bgcolor="primary.100"
          color="primary.main"
          display="inline-block"
          className="hover:bg-primary hover:text-white"
          p={0.5}
          lineHeight={0}
        >
          <KeyboardArrowRight />
        </Box>
        <Box
          borderRadius="4px"
          bgcolor="primary.100"
          color="primary.main"
          display="inline-block"
          className="hover:bg-primary hover:text-white"
          p={0.5}
          lineHeight={0}
          onClick={(event) => {
            onDeleteTemplate?.(item.id);
            event.stopPropagation();
          }}
        >
          <Delete />
        </Box>
      </div>
    ),
    onClickRow: (item) => onClickTemplate?.(item.id),
    endingIconCellProps: {
      style: {
        verticalAlign: "top",
        width: "200px",
      },
      width: "200px",
      align: "right",
    },
  };

  return (
    <div className="border border-grey-300 rounded-lg">
      <TableView
        items={items}
        columns={columns}
        rows={rows}
        isLoading={loading}
        tableContainerProps={{ style: { borderTopRightRadius: "8px", borderTopLeftRadius: "8px" } }}
      />
      {paginationOptions && (
        <CursorPaginationTableControlsView
          {...paginationOptions}
          style={{ borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}
        />
      )}
    </div>
  );
};

interface TemplateStopCellViewProps {
  item: ShipperQuoteRequest;
  position: "origin" | "destination";
  hideTemplateName?: boolean;
}

const TemplateStopCellView: VFC<TemplateStopCellViewProps> = ({ item, position }) => {
  const stops = item.stops;
  const origin = arrayAt(stops, 0);
  const destination = arrayAt(stops, stops.length - 1);
  const stop = position === "origin" ? origin : destination;
  if (!stop) {
    return null;
  }

  const originLabels = getStopLabels({ ...origin, address: origin?.address ?? null, position: 1 }, item.mode);
  const destinationLabels = getStopLabels(
    { ...destination, address: destination?.address ?? null, position: stops.length },
    item.mode
  );
  const stopLabels = getStopLabels({ ...stop, position: position === "origin" ? 1 : stops.length }, item.mode);

  const displaySubLabel = !!originLabels.subLabel || !!destinationLabels.subLabel;

  return (
    <>
      <div className="flex justify-start gap-1">
        <Text size="small" weight="bold" typographyProps={{ gutterBottom: true }}>
          {stopLabels.label}
        </Text>
        {position === "origin" && (
          <MultistopTooltip
            item={{
              ...item,
              routing: { short: item.routing_type ?? "", long: item.routing_type ?? "" },
              incoterms: { short: item.incoterms ?? "", long: item.incoterms ?? "" },
              stops: item.stops.map((stop, index) => ({ ...stop, position: index + 1 })),
            }}
          />
        )}
      </div>

      {!!displaySubLabel && <Text size="small">{stopLabels.subLabel || NON_BREAKING_SPACE}</Text>}
    </>
  );
};

interface TemplateEquipmentCellViewProps {
  item: ShipperQuoteRequest;
}

const TemplateEquipmentCellView: VFC<TemplateEquipmentCellViewProps> = ({ item }) => (
  <Text size="small">
    {isEmpty(item.equipment) ? (
      EMPTY_CELL_HYPHEN
    ) : (
      <>
        {item.equipment.map((row) => (
          <>
            {row}
            <br />
          </>
        ))}
      </>
    )}
  </Text>
);

export default TemplatesTableView;
